import { MultiStepFormConfig, BaseFormData } from './components/types';
import { AbnLookupStep, BusinessDetailsStep, ContactDetailsStep, MultiInputStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  criteriaStepInitialData, 
  abnLookupStepInitialData, 
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  combineStepData,
  multiInputStepInitialData
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({
  suitabilityCriteria: [
    "An ABN or ACN registered in Australia",
    "A business email address linked to your trading name",
    "A recent business bank statement (e.g. front page)",
    "A valid driver’s licence or passport"
  ],
});

export const formValidations = createFormValidations({
  businessDetailsStep: (data: BaseFormData) => {
    // Basic validation for business details
    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');
    return !!(data.countryOfRegistration && data.bestDescribesYou && stateValid);
  },
  frequentFlyerValidation: () => {
    return true;
  },
  // contactDetailsStep: (data: BaseFormData) => {
  //   // Basic required fields
  //   const basicFieldsValid = !!(data.firstName && data.lastName && data.workEmail && data.phone && data.howDidYouHear);
    
  //   // Conditional validation for referredBy field
  //   const shouldRequireReferredBy = data.howDidYouHear === "Business referral (e.g. accountant, client, partner)" ||
  //                                  data.howDidYouHear === "Financial adviser or broker";
    
  //   const referredByValid = shouldRequireReferredBy 
  //     ? !!(data.referredBy && typeof data.referredBy === 'string' && data.referredBy.trim().length >= 2)
  //     : true; // Not required if condition not met
    
  //   return basicFieldsValid && referredByValid;
  // },
});

// Combine initial data from selected steps
const initialFormData = combineStepData(
  criteriaStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  multiInputStepInitialData
  // Add more step data here as you add more steps
);

export const partnerProgramFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'partner-program',
  formOptions: formOptions,
  helperText: {
    firstStep: 'Takes under 2 minutes.',
    lastStep: 'Our team will get back to you within 1 business day'
  },
  steps: [
    {
      id: 'business-search',
      title: 'First, let’s find your referral business.',
      description: 'Search your business so we can set up your partner record and link future referrals to you.',
      validation: formValidations.abnLookupStep,
      component: AbnLookupStep,
    },
    {
      id: 'business-details',
      title: 'Help us get to know your business.',
      description: 'These details help us tailor the partnership experience for <span className="text-brand font-bold">{{businessName}}</span>.',
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessDescription: true,
        showBestDescribesYou: true,
        // showNumberOfClients: true,
      },
      validation: formValidations.businessDetailsStep,
      component: BusinessDetailsStep,
    },
    {
      id: 'frequent-flyer',
      title: 'Earn Frequent Flyer Points on eligible referrals.',
      description: 'Enter your membership number if you want to collect points. You can also provide this later.',
      component: MultiInputStep,
      // shouldShow: (data: BaseFormData) => data.countryOfRegistration === 'Australia',
      validation: formValidations.frequentFlyerValidation,
      stepConfig: {
        inputFields: [
          {
            fieldName: 'qantasBusinessRewards',
            label: 'Qantas Business Rewards number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true, 
            helperText: 'Not a member yet? Join <a href="https://www.qantas.com/au/en/business-rewards/partner/join.html?code=PFOCTET" target="_blank">Qantas Business Rewards</a>',
          },
          {
            fieldName: 'virginAustraliaBusinessFlyer',
            label: 'Virgin Australia Business Flyer number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true,
            helperText: 'Not a member yet? Join <a href="https://www.virginaustralia.com/au/en/fly-for-business/business-flyer/business-flyer-application/?promocode=vf-par-adob-cta1-1137" target="_blank">Virgin Australia Business Flyer</a>',
          }
        ]
      }
    },
    {
      id: 'contact-details',
      title: 'Tell us who we work with.',
      description: 'Confirm your details. We assign a BDM to you and contact you to guide you through the next steps.',
      stepConfig: {
        showTitle: false,
        showFirstName: true,
        showLastName: true,
        showWorkEmail: true,
        showPhone: true,
        showHowDidYouHear: true,
        showMessage: true,
        showTermsAccepted: true,
        showMarketingConsent: true,
        messageHelperText: "Add information about a current referral, or any other details you want our BDM team to know before we contact you.",
      },
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your business needs and next steps.',
    firstName: '{{firstName}}',
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}',
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our specialist team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'View Homepage',
      href: '/'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful trends, insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };

      // Map form data to Dynamics field format
      const dynamicsPayload = {
        ot_topic: 'Partner Program application',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_industry: (data.industry === 'Other' ? (data.customIndustry as string) || '' : (data.industry as string) || ''),
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        pb_businessdescription: (data.businessDescription as string) || '',
        pb_bestdescribesyou: (data.bestDescribesYou as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Frequent Flyer details
        pb_qantasbusinessrewards: (data.qantasBusinessRewards as string) || '',
        pb_virginaustraliabusinessflyer: (data.virginAustraliaBusinessFlyer as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        // UTM parameters
        ...utmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true,
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Partner Program Form - Step ${currentStep} of ${totalSteps}`);
  },
};
